import Yup from '~/util/yup';
import { FormModal } from '~/shared/forms/FormModal';
import { editLabOptions, LabCollectionTechniqueOptions, type FullOrderViewModel } from '@generated';
import { AutocompleteSearchFormField } from '~/shared/forms/controls/fields/AutocompleteSearchFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { getInvalidateOrderQueryKeys } from '~/features/orders/util';
import { AddLabFrequenciesFormControl } from './AddLabFrequenciesFormControl';

const validationSchema = Yup.object().shape({
	indication: Yup.string().label('Indication'),
	icdDiagnosisId: Yup.string()
		.label('ICD Diagnosis')
		.when('indication', {
			is: (indication: string) => !indication || indication.trim().length === 0,
			then: schema => schema.required('ICD Diagnosis is required when text field is blank'),
			otherwise: schema => schema.nullable()
		}),
	lab: Yup.object().shape({
		labDefinitionId: Yup.string().label('Lab Definition').required(),
		requiresTechniqueSelection: Yup.boolean(),
		labCollectionTechniqueId: Yup.string()
			.label('Lab Collection Technique')
			.when('requiresTechniqueSelection', {
				is: true,
				then: schema => schema.required('Lab Collection Technique is required'),
				otherwise: schema => schema.nullable()
			}),
		requiresSource: Yup.boolean(),
		source: Yup.string()
			.label('Source')
			.when('requiresSource', {
				is: true,
				then: schema => schema.required(),
				otherwise: schema => schema.nullable()
			})
	}),
	labFrequencies: Yup.array()
		.label('Lab Frequencies')
		.of(
			Yup.object().shape({
				scheduledDateAndTime: Yup.date().label('Scheduled Date and Time').required(),
				frequencyCadenceTypeId: Yup.number().label('Frequency Cadence Type').required(),
				assignedToId: Yup.string().label('Assigned To').nullable(),
				notes: Yup.string().label('Notes').nullable()
			})
		)
});

export function EditLabFormModal({ fullOrder, labId, closeModal }: { fullOrder: FullOrderViewModel; closeModal: () => void; labId: string }) {
	const selectedLab = fullOrder.labs.map(x => x.lab).find(x => x.id === labId);
	const selectedLabDraws = fullOrder.labs.filter(x => x.lab.id === labId);

	if (!selectedLab) {
		throw new Error(`Lab with id ${labId} not found in order ${fullOrder.id}`);
	}

	return (
		<FormModal
			title="Edit Lab"
			initialValues={{
				orderId: fullOrder.id,
				indication: selectedLab.indication,
				icdDiagnosisId: selectedLab.icdDiagnosis,
				lab: selectedLab,
				labFrequencies: selectedLabDraws
			}}
			saveMutation={{
				...editLabOptions,
				invalidateQueryKeys: [...getInvalidateOrderQueryKeys(fullOrder)]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
			size="xl"
		>
			{formHelpers => {
				return (
					<div className="flex flex-col gap-8">
						<div className="flex flex-col gap-2">
							<div className="flex items-center justify-between rounded border px-3 py-2">
								<div className="flex flex-row items-center gap-4">
									<span className="font-medium text-gray-900">{selectedLab.labDefinition.name}</span>
									{selectedLab.labDefinition.requiresTechniqueSelection && (
										<SelectFormField
											hideLabel
											name="lab.labCollectionTechniqueId"
											options={LabCollectionTechniqueOptions}
											placeholder="Lab Collection Technique"
										/>
									)}
									{selectedLab.labDefinition.requiresSource && <TextFormField name="lab.source" label="Source" />}
								</div>
							</div>
						</div>
						<div className="grid grid-cols-3 gap-2">
							<AutocompleteSearchFormField searchType="icd-diagnosis" name="icdDiagnosisId" />
							<TextFormField name="indication" />
						</div>
						<AddLabFrequenciesFormControl fullOrder={fullOrder} formHelpers={formHelpers} />
					</div>
				);
			}}
		</FormModal>
	);
}
